// File: /src/contexts/AuthContext.js

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { isAxiosError } from 'axios';
import { isTokenValid } from '../utils/auth';
import apiClient, {
	setApiClientLogoutHandler,
} from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [token, setToken] = useState(() => localStorage.getItem('authToken'));
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(Boolean(token));
	const [error, setError] = useState(null);

	const clearAuthState = useCallback(() => {
		setToken(null);
		setUser(null);
		setError(null);
		setLoading(false);
	}, []);

	const logout = useCallback(() => {
		localStorage.removeItem('authToken');
		clearAuthState();
		window.dispatchEvent(new Event('auth:logout'));
	}, [clearAuthState]);

	const fetchUser = useCallback(
		async (activeToken) => {
			if (!activeToken) return null;

			setLoading(true);
			try {
				const response = await apiClient.get(API_ENDPOINTS.USERS_PROFILE, {
					headers: {
						Authorization: `Bearer ${activeToken}`,
					},
				});
				setUser(response.data);
				setError(null);
				return response.data;
			} catch (err) {
				setUser(null);

				if (isAxiosError(err) && err.response?.status === 401) {
					logout();
				} else {
					setError(err);
				}

				throw err;
			} finally {
				setLoading(false);
			}
		},
		[logout]
	);

	const refreshUser = useCallback(() => {
		if (!token) return Promise.resolve(null);
		return fetchUser(token);
	}, [fetchUser, token]);

	const login = useCallback(
		async (newToken, userData = null) => {
			localStorage.setItem('authToken', newToken);
			setToken(newToken);

			if (userData) {
				setUser(userData);
				setError(null);
				setLoading(false);
				return userData;
			}

			return fetchUser(newToken);
		},
		[fetchUser]
	);

	useEffect(() => {
		setApiClientLogoutHandler(logout);

		if (!token) {
			setLoading(false);
			return;
		}

		if (!isTokenValid(token)) {
			logout();
			return;
		}

		if (!user) {
			fetchUser(token).catch(() => {
				// fetchUser already handles state updates/logouts
			});
		} else {
			setLoading(false);
		}
		return () => {
			setApiClientLogoutHandler(null);
		};
	}, [token, user, fetchUser, logout]);

	useEffect(() => {
		const handleStorage = (event) => {
			if (event.key === 'authToken') {
				setToken(event.newValue);
			}
		};

		const handleExternalLogout = () => {
			clearAuthState();
		};

		window.addEventListener('storage', handleStorage);
		window.addEventListener('auth:logout', handleExternalLogout);

		return () => {
			window.removeEventListener('storage', handleStorage);
			window.removeEventListener('auth:logout', handleExternalLogout);
		};
	}, [clearAuthState]);

	const value = useMemo(
		() => ({
			token,
			user,
			error,
			loading,
			isAuthenticated: Boolean(user),
			login,
			logout,
			refreshUser,
		}),
		[token, user, error, loading, login, logout, refreshUser]
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth deve ser usado dentro de um AuthProvider');
	}
	return context;
};
